/**
 * Summary service — calls the AETHER backend, which invokes the summary tool
 * Lambda server-side. The user's Cognito token authenticates to our API (via
 * the shared authed client); the summarizer itself is reached server-side.
 */
import { createLogger } from '../utils/logger';
import { authedFetch } from './apiClient';

const logger = createLogger('SummaryService');

/** Shape returned by the generate-summary endpoint. */
export interface SummaryResult {
  summary: string;
  fastCheckPassed?: boolean;
  wasRetried?: boolean;
}

function isSummaryResult(value: unknown): value is SummaryResult {
  return (
    typeof value === 'object' &&
    value !== null &&
    'summary' in value &&
    typeof value.summary === 'string'
  );
}

/**
 * Generates a summary for an event from its concatenated notes. `eventId` is
 * used for correlation/logging by the tool (a placeholder is fine in create
 * mode). `previousSummary` is the latest stored summary, if any. Throws on a
 * failed request so the caller can surface the error to the user.
 */
export async function generateSummary(
  eventId: string,
  notes: string,
  previousSummary?: string,
  store: boolean = false,
): Promise<SummaryResult> {
  const id: string = eventId || 'unsaved-event';
  try {
    const response: Response | null = await authedFetch(
      `/events/${encodeURIComponent(id)}/summarize`,
      {
        method: 'POST',
        includeContentType: true,
        body: JSON.stringify({ notes, previousSummary: previousSummary || '', store }),
      },
    );

    if (!response) throw new Error('Summary request could not be authenticated');
    if (!response.ok) {
      const errorBody: string = await response.text();
      logger.error('Summary API returned error', { status: response.status, body: errorBody });
      throw new Error(`Summary generation failed (${response.status})`);
    }
    const result: unknown = await response.json();
    if (!isSummaryResult(result)) throw new Error('Summary API returned an invalid response');
    return result;
  } catch (error: unknown) {
    const message: string = error instanceof Error ? error.message : 'Unknown error';
    logger.error('Summary request failed', { eventId: id, error: message });
    if (error instanceof Error) throw error;
    throw new Error(message);
  }
}

/**
 * Fire-and-forget background summary for an IN_WORK update: generates the
 * summary from the current notes and stores it server-side (summary-history)
 * without blocking the user's save/close. Best-effort — a failure is logged and
 * swallowed so it never affects the update. Requires a saved event id.
 */
export async function generateAndStoreSummaryInBackground(
  eventId: string,
  notes: string,
  previousSummary?: string,
): Promise<void> {
  try {
    await generateSummary(eventId, notes, previousSummary, true);
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    logger.error('Background summary generation failed', { eventId, error: message });
  }
}

/**
 * Concatenates an event's note entries into a single chronological notes string
 * for the summary tool. Oldest first, each line prefixed with its Zulu
 * timestamp so the summarizer can build an accurate timeline.
 */
export function concatNotesForSummary(
  noteEntries: { date: string; time: string; text: string }[],
): string {
  return [...noteEntries]
    .sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`))
    .map((n) => {
      const stamp = `${n.date} ${n.time}`.trim();
      return stamp ? `[${stamp}z] ${n.text}` : n.text;
    })
    .filter((line) => line.trim())
    .join('\n');
}
